import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FloatingBackToTop from '@/components/feature/FloatingBackToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function ArticleDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-16 md:pt-20 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm mb-4">{t('articleNotFound')}</p>
              <Link to="/articles" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-300">
                {t('backToArticles')}
              </Link>
            </div>
          </main>
          <Footer />
          <FloatingBackToTop />
        </div>
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${article.title} - 岛国精品`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://www.daoguojingpin.com/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
      />

      <SchemaOrg type="Article" data={{
        headline: article.title,
        description: article.excerpt,
        image: article.coverImage,
        datePublished: article.publishedAt,
        author: { '@type': 'Organization', name: '岛国精品' },
        publisher: { '@type': 'Organization', name: '岛国精品' },
      }} />

      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.daoguojingpin.com' },
          { '@type': 'ListItem', position: 2, name: '精选文章', item: 'https://www.daoguojingpin.com/articles' },
          { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.daoguojingpin.com/article/${article.slug}` },
        ],
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 md:pt-20">
          <Breadcrumb
            items={[
              { label: t('pageTitle'), path: '/articles' },
              { label: article.title },
            ]}
          />

          <article className="w-full px-4 md:px-6 py-8">
            <div className="max-w-3xl mx-auto">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-time-line"></i>
                    {article.readTime}
                  </span>
                  <span className="text-xs text-foreground-400">
                    {t('publishedOn')} {article.publishedAt}
                  </span>
                </div>
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                  {article.excerpt}
                </p>
              </div>

              <div className="mb-8 aspect-[16/9] rounded-lg overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                  [&_h3]:font-heading [&_h3]:text-lg [&_h3]:md:text-xl [&_h3]:font-bold [&_h3]:text-foreground-900 [&_h3]:mt-8 [&_h3]:mb-4
                  [&_p]:mb-4 [&_p]:leading-relaxed
                  [&_strong]:text-foreground-900"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-12 pt-8 border-t border-background-200">
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <span className="text-xs text-foreground-500">{t('tagLabel')}:</span>
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center gap-3">
                  <span className="text-xs text-foreground-500">{t('shareLabel')}:</span>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-accent-100 hover:text-accent-700 transition-all duration-200">
                    <i className="ri-wechat-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-accent-100 hover:text-accent-700 transition-all duration-200">
                    <i className="ri-weibo-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-accent-100 hover:text-accent-700 transition-all duration-200">
                    <i className="ri-link text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-12 bg-background-100">
              <div className="max-w-3xl mx-auto">
                <h3 className="font-heading text-lg font-bold text-foreground-900 mb-6">
                  {t('relatedArticles')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.slug}`}
                      className="group bg-background-50 rounded-lg overflow-hidden hover:bg-background-100 transition-all duration-300"
                    >
                      <div className="aspect-[16/9] overflow-hidden">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="font-heading text-xs font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-300 line-clamp-2">
                          {ra.title}
                        </h4>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          <div className="w-full px-4 md:px-6 py-8 text-center">
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-foreground-300 text-foreground-700 text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-all duration-300"
            >
              {t('backToArticles')}
              <i className="ri-arrow-left-line"></i>
            </Link>
          </div>
        </main>
        <Footer />
        <FloatingBackToTop />
      </div>
    </>
  );
}